/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { GanttRow, GanttTask } from '#UNSAFE_Gantt/Gantt.types';
import type { getRowDataLayout } from './dataLayoutUtils';

type RowDataLayout<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>> = ReturnType<
  typeof getRowDataLayout<K1, K2, D1, D2>
>;

/**
 * Gets the default task to initially receive logical focus upon component focus.
 * @param param0 The (unresolved) row data layout.
 * @returns The task (if there are any) to receive focus.
 */
const getDefaultInitialFocusTask = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>({
  rowObjs
}: RowDataLayout<K1, K2, D1, D2>) => {
  // Default to the first task of the first row (if it exists)
  if (rowObjs.length === 0) return;
  if (rowObjs[0].taskObjs.length === 0) return;
  return { id: rowObjs[0].taskObjs[0].id, isFocused: true };
};

/**
 * Gets next or previous navigable task from the current task.
 * @param currentTask The current task.
 * @param param1 The (unresolved) row data layout.
 * @param direction 'next' or 'prev'
 * @returns The adjacent navigable task according to the given direction.
 */
const getAdjacentTaskNavigable = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  currentTask: K2,
  { rowObjs, idTaskObjMap }: RowDataLayout<K1, K2, D1, D2>,
  direction: 'next' | 'prev'
) => {
  const currentTaskObj = idTaskObjMap.get(currentTask);
  if (!currentTaskObj) return;

  const indexOffset = direction === 'next' ? 1 : -1;
  const { rowIndex, renderIndex: index } = currentTaskObj;

  // Return adjacent task in the row if it exists
  const adjTaskObj = rowObjs[rowIndex].taskObjs[index + indexOffset];
  if (adjTaskObj) return adjTaskObj.id;

  // Return a task in adjacent row if it exists
  // For 'next' direction, this is the first task of the next row
  // For 'prev' direction, this is the last task of the previous row
  const adjRowObj = rowObjs[rowIndex + indexOffset];
  if (adjRowObj) {
    const adjTaskObj =
      direction === 'next'
        ? adjRowObj.taskObjs[0]
        : adjRowObj.taskObjs[adjRowObj.taskObjs.length - 1];
    if (adjTaskObj) return adjTaskObj.id;
  }

  return;
};

/**
 * Gets above or below navigable task from the current task.
 * @param currentTask The current task.
 * @param param1 The (unresolved) row data layout.
 * @param direction 'up' or 'down'
 * @returns The adjacent navigable task according to the given direction.
 */
const getVerticalTaskNavigable = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  currentTask: K2,
  { rowObjs, idTaskObjMap }: RowDataLayout<K1, K2, D1, D2>,
  direction: 'up' | 'down'
) => {
  const currentTaskObj = idTaskObjMap.get(currentTask);
  if (!currentTaskObj) return;

  const rowIndexOffset = direction === 'down' ? 1 : -1;
  const { rowIndex } = currentTaskObj;

  // Return first task in the adjacent row if it exists
  const adjRowObj = rowObjs[rowIndex + rowIndexOffset];
  if (!adjRowObj) return;

  const adjTaskObj = adjRowObj.taskObjs[0];
  if (adjTaskObj) return adjTaskObj.id;

  return;
};

/**
 * Gets the next navigable task from the current task.
 * @param currentTask The current task.
 * @param rowDataLayout The (unresolved) row data layout.
 * @returns The next navigable task.
 */
const getNextTaskNavigable = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  currentTask: K2,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>
) => getAdjacentTaskNavigable(currentTask, rowDataLayout, 'next');

/**
 * Gets the previous navigable task from the current task.
 * @param currentTask The current task.
 * @param rowDataLayout The (unresolved) row data layout.
 * @returns The previous navigable task.
 */
const getPrevTaskNavigable = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  currentTask: K2,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>
) => getAdjacentTaskNavigable(currentTask, rowDataLayout, 'prev');

/**
 * Gets the navigable task above the current task.
 * @param currentTask The current task.
 * @param rowDataLayout The (unresolved) row data layout.
 * @returns The navigable task above.
 */
const getAboveTaskNavigable = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  currentTask: K2,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>
) => getVerticalTaskNavigable(currentTask, rowDataLayout, 'up');

/**
 * Gets the navigable task below the current task.
 * @param currentTask The current task.
 * @param rowDataLayout The (unresolved) row data layout.
 * @returns The navigable task below.
 */
const getBelowTaskNavigable = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  currentTask: K2,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>
) => getVerticalTaskNavigable(currentTask, rowDataLayout, 'down');

/**
 * Gets above or below navigable row label from the current row label.
 * @param currentRowLabel The current row label.
 * @param param1 The (unresolved) row data layout.
 * @param direction 'up' or 'down'
 * @returns The adjacent navigable row label according to the given direction.
 */
const getAdjacentRowLabelNavigable = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  currentRowLabel: K1,
  { rowObjs, idRowObjMap }: RowDataLayout<K1, K2, D1, D2>,
  direction: 'up' | 'down'
) => {
  const currentRowObj = idRowObjMap.get(currentRowLabel);
  if (!currentRowObj) return;

  const rowIndexOffset = direction === 'down' ? 1 : -1;
  const { index: rowIndex } = currentRowObj;
  const adjRowObj = rowObjs[rowIndex + rowIndexOffset];
  if (!adjRowObj) return;

  return adjRowObj.id;
};

/**
 * Gets the navigable row label above the current row label.
 * @param currentRowLabel The current row label.
 * @param rowDataLayout The (unresolved) row data layout.
 * @returns The navigable row label above.
 */
const getAboveRowLabelNavigable = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  currentRowLabel: K1,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>
) => getAdjacentRowLabelNavigable(currentRowLabel, rowDataLayout, 'up');

/**
 * Gets the navigable row label below the current row label.
 * @param currentRowLabel The current row label.
 * @param rowDataLayout The (unresolved) row data layout.
 * @returns The navigable row label below.
 */
const getBelowRowLabelNavigable = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  currentRowLabel: K1,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>
) => getAdjacentRowLabelNavigable(currentRowLabel, rowDataLayout, 'down');

/**
 * Gets the row label navigable from the current task.
 * @param currentTask The current task.
 * @param rowDataLayout The (unresolved) row data layout.
 * @returns The row label navigable from the current task.
 */
const getRowLabelNavigableFromTask = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  currentTask: K2,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>
) => {
  // taskObj should not be undefined because you must start from
  // a focused task to get to a rowlabel.
  const taskObj = rowDataLayout.idTaskObjMap.get(currentTask)!;
  const { rowIndex } = taskObj;
  const rowObj = rowDataLayout.rowObjs[rowIndex];
  return rowObj.id;
};

/**
 * Gets the task navigable from the current row label.
 * @param currentRowLabel The current row label.
 * @param rowDataLayout The (unresolved) row data layout.
 * @param currentTask If any, the last current task (e.g. the last focused task).
 * @returns The task navigable from the current row label.
 */
const getTaskNavigableFromRowLabel = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  currentRowLabel: K1,
  rowDataLayout: RowDataLayout<K1, K2, D1, D2>,
  currentTask?: K2
) => {
  if (currentTask !== undefined) {
    const taskObj = rowDataLayout.idTaskObjMap.get(currentTask);
    if (taskObj) {
      const { rowIndex } = taskObj;
      const rowObj = rowDataLayout.rowObjs[rowIndex];
      if (rowObj.id === currentRowLabel) {
        return currentTask;
      }
    }
  }
  const rowObj = rowDataLayout.idRowObjMap.get(currentRowLabel);
  const firstTaskObj = rowObj?.taskObjs[0];
  if (firstTaskObj) {
    return firstTaskObj.id;
  }
  const defaultTask = getDefaultInitialFocusTask(rowDataLayout);
  return defaultTask?.id;
};

/**
 * Gets the dx and dy necessary to pan the given region into view.
 * @param region The target region to pan into view.
 * @param viewportRegion The viewport region.
 * @param isRTL Whether the reading direction is rtl.
 * @param xPriority The side in the x direction to prioritize scroll into view.
 * @param yPriority The side in the y direction to prioritize scroll into view.
 * @param overShoot The extra amount of space to pan by.
 * @returns The dx and dy values.
 */
const getPanIntoViewValues = (
  region: { x: number; y: number; w: number; h: number },
  viewportRegion: { x: number; y: number; w: number; h: number },
  isRTL: boolean,
  xPriority: 'start' | 'end' | 'auto' = 'auto',
  yPriority: 'top' | 'bottom' | 'auto' = 'auto',
  overShoot = 0
) => {
  const deltaXLeftVisible = Math.min(region.x - viewportRegion.x - overShoot, 0);
  const deltaXRightVisible = Math.max(
    0,
    region.x + region.w - (viewportRegion.x + viewportRegion.w) + overShoot
  );
  const deltaXStartVisible = isRTL ? deltaXRightVisible : deltaXLeftVisible;
  const deltaXEndVisible = isRTL ? deltaXLeftVisible : deltaXRightVisible;

  let deltaX = 0;
  switch (xPriority) {
    case 'start':
      deltaX = deltaXStartVisible;
      break;
    case 'end':
      deltaX = deltaXEndVisible;
      break;
    case 'auto':
      deltaX = (!isRTL ? deltaXEndVisible > 0 : deltaXEndVisible < 0)
        ? deltaXEndVisible
        : deltaXStartVisible; // 'end' wins if just 'end', or both sides, require panning. 'start' otherwise.
      break;
    default: {
      const _exhaustiveCheck: never = xPriority;
      return _exhaustiveCheck;
    }
  }

  const deltaYTopVisible = Math.min(region.y - viewportRegion.y - overShoot, 0);
  const deltaYBottomVisible = Math.max(
    0,
    region.y + region.h - (viewportRegion.y + viewportRegion.h) + overShoot
  );

  let deltaY = 0;
  switch (yPriority) {
    case 'top':
      deltaY = deltaYTopVisible;
      break;
    case 'bottom':
      deltaY = deltaYBottomVisible;
      break;
    case 'auto':
      deltaY = deltaYTopVisible < 0 ? deltaYTopVisible : deltaYBottomVisible; // 'top' wins if just 'top', or both sides, require panning. 'bottom' otherwise.
      break;
    default: {
      const _exhaustiveCheck: never = yPriority;
      return _exhaustiveCheck;
    }
  }

  return { dx: -deltaX, dy: -deltaY };
};

export {
  getDefaultInitialFocusTask,
  getNextTaskNavigable,
  getPrevTaskNavigable,
  getAboveTaskNavigable,
  getBelowTaskNavigable,
  getPanIntoViewValues,
  getAboveRowLabelNavigable,
  getBelowRowLabelNavigable,
  getRowLabelNavigableFromTask,
  getTaskNavigableFromRowLabel
};
